import { defineStore } from 'pinia';
import { 
  ApplicationState, 
  AlertType, 
  NotificationParams, 
  NotificationState,
  NotificationSystemState,
  Position,
  PositionStackConfig,
  Banner, 
  BannerTheme, 
  BannerPriority, 
  BannerAction 
} from '@/types/store';
import { RouteRecordNameGeneric } from 'vue-router';
import { v4 as uuidv4 } from 'uuid';
import { isMobile } from '@/utils/mobileDetect';

const initialState = (): ApplicationState => ({
  alert: {
    show: false,
    type: 'error',
    text: '',
    icon: '',
    additionalInfo: [],
  },
  initialLoad: true,
  sidebarOpen: false,
  isAppReady: false,
  isPageReady: true, // Default to true, guards will set to false when loading
  routeLoadingStates: {},
  isWideSidebar: false,
  currentPage: 1,
  registrationFieldsLoaded: false,
  importUsersErrorsLoaded: false,
  currentTab: 0,
  currentTabProps: null,
  analyticsShowLimitedView: false,
  activeDay: null,
  demoProducts: false,
  // Legacy notification support (backward compatibility)
  notifications: [],
  notification: {
    isOpen: false,
    title: '',
    message: '',
    theme: 'primary',
    position: 'br',
    autoClose: true,
    createdAt: Date.now()
  },
  // Enhanced notification system
  notificationSystem: {
    notifications: [],
    maxPerPosition: 5,
    globalPaused: false,
    layoutAware: true,
    stackConfigs: {
      'tr': {
        maxVisible: 5,
        stackDirection: 'down',
        spacing: 12,
        offset: { x: 24, y: 24 },
        respectsSidebar: false,
        stackingMode: 'linear',
        cardStackThreshold: 3,
        cardOffsetX: 6,
        cardOffsetY: 12
      },
      'tl': {
        maxVisible: 5,
        stackDirection: 'down',
        spacing: 12,
        offset: { x: 24, y: 24 },
        respectsSidebar: true,
        stackingMode: 'linear',
        cardStackThreshold: 3,
        cardOffsetX: -6,
        cardOffsetY: 12
      },
      'br': {
        maxVisible: 5,
        stackDirection: 'up',
        spacing: 12,
        offset: { x: 24, y: 24 },
        respectsSidebar: false,
        stackingMode: 'linear',
        cardStackThreshold: 3,
        cardOffsetX: 6,
        cardOffsetY: -12
      },
      'bl': {
        maxVisible: 5,
        stackDirection: 'up',
        spacing: 12,
        offset: { x: 24, y: 24 },
        respectsSidebar: true,
        stackingMode: 'linear',
        cardStackThreshold: 3,
        cardOffsetX: -6,
        cardOffsetY: -12
      },
      'tc': {
        maxVisible: 3,
        stackDirection: 'down',
        spacing: 12,
        offset: { x: 0, y: 24 },
        respectsSidebar: false,
        stackingMode: 'linear',
        cardStackThreshold: 3,
        cardOffsetX: 0,
        cardOffsetY: 12
      },
      'bc': {
        maxVisible: 3,
        stackDirection: 'up',
        spacing: 12,
        offset: { x: 0, y: 24 },
        respectsSidebar: false,
        stackingMode: 'linear',
        cardStackThreshold: 3,
        cardOffsetX: 0,
        cardOffsetY: -12
      }
    }
  },
  dynamicHeaderComponent: null,
  dynamicHeaderScale: 'scale-100',
  loadingTitle: 'Loading...',
  loadingSubtitle: '',
  isRouterNavigating: false,
  isHistoryNavigation: false,
  // Legacy banner support (maintain backward compatibility)
  showDynamicBanner: false,
  dynamicBanner: null,
  // Enhanced banner system
  bannerState: {
    banners: [],
    maxVisible: 3,
    defaultPosition: 'top'
  },
  loadingTimeoutId: null,
  loadingStartTime: null
})

export const useApplicationStore = defineStore({
  id: 'application',
  state: initialState,
  actions: {
    setState<K extends keyof ApplicationState>(payload: { key: K; value: ApplicationState[K] }) {
      const { key, value } = payload;
      this.$state[key] = value;
    },
    reset() {
      Object.assign(this.$state, initialState);
      Object.keys(initialState).forEach((key) => {
        localStorage.removeItem(key);
      });
    },
    setNestedState(path: string, value: any) {
      const keys = path.split('.');
      let stateRef: any = this.$state;

      keys.forEach((key, index) => {
        // Check if the key is an array index
        const arrayMatch = key.match(/^(\w+)\[(\d+)\]$/);

        if (arrayMatch) {
          const arrayKey = arrayMatch[1];
          const arrayIndex = parseInt(arrayMatch[2], 10);

          if (!stateRef[arrayKey]) {
            this.$patch(() => {
              stateRef[arrayKey] = [];
            });
          }

          if (index === keys.length - 1) {
            // Directly set the value if it's the last key
            this.$patch(() => {
              stateRef[arrayKey][arrayIndex] = value;
            });
          } else {
            // Traverse deeper into the array element
            stateRef = stateRef[arrayKey][arrayIndex];
          }
        } else {
          if (index === keys.length - 1) {
            // Set the value if it's the last key
            this.$patch(() => {
              stateRef[key] = value;
            });
          } else {
            if (!stateRef[key]) {
              this.$patch(() => {
                stateRef[key] = {};
              });
            }
            stateRef = stateRef[key];
          }
        }
      });
    },
    showAlert(type: AlertType, text: string, icon: string, additionalInfo: string[]) {
      this.alert.show = true;
      this.alert.type = type;
      this.alert.text = text;
      this.alert.icon = icon;
      this.alert.additionalInfo = additionalInfo;
    },
    hideAlert() {
      this.alert.show = false;
    },
    openSidebar() {
      this.sidebarOpen = true;
    },
    closeSidebar(routeName?: RouteRecordNameGeneric) {
      if (routeName && routeName !== 'Admin' && routeName !== 'Portal') {
        this.sidebarOpen = false;
      }
    },
    expandWideSidebar() {
      this.isWideSidebar = true;
    },
    reduceWideSidebar() {
      this.isWideSidebar = false;
    },
    toggleIsAppReady() {
      this.isAppReady = !this.isAppReady;
    },
    incrementCurrentPage() {
      this.currentPage++;
    },
    generateShortUID(): string {
      return uuidv4().substr(0, 4); // Generate a UUID and take the first 4 characters
    },
    setLoading(val: boolean) {
      this.isAppReady = val;
    },
    setRouteLoadingState(routeName: string, isLoading: boolean) {
      this.routeLoadingStates = {
        ...this.routeLoadingStates,
        [routeName]: isLoading,
      };
    },
    // Reset active tab index to the first tab (index 0)
    resetCurrentTab() {
      this.setState({ key: 'currentTab', value: 0 });
    },
    getRouteLoadingState(routeName: string) {
      return this.routeLoadingStates[routeName] || false;
    },
    showNotification(notification: NotificationParams) {
      // Dedupe logic: avoid stacking identical notifications repeatedly
      const now = Date.now();
      const safeLower = (val: unknown, fallback = ''): string => {
        if (typeof val === 'string') return val.toLowerCase();
        try {
          // Handle objects/errors that may supply message/name
          if (val && typeof val === 'object') {
            const maybeMessage = (val as any).message ?? (val as any).name;
            if (typeof maybeMessage === 'string') return maybeMessage.toLowerCase();
          }
          return String(val ?? fallback).toLowerCase();
        } catch {
          return String(fallback).toLowerCase();
        }
      };

      const computeDedupeKey = (n: Partial<NotificationParams>) => {
        if (n.dedupeKey) {
          return safeLower(n.dedupeKey, '');
        }
        const t = safeLower(n.title, '');
        const m = safeLower(n.message, '');
        const th = safeLower(n.theme || 'primary', 'primary');
        const p = safeLower(n.position || 'br', 'br');
        return `${t}|${m}|${th}|${p}`;
      };

      const incomingKey = computeDedupeKey(notification);
      // Longer consolidation window for session expired; shorter for others (unless caller overrides)
      const isSessionExpired = safeLower(notification.title, '').includes('session expired')
        || safeLower(notification.message, '').includes('session expired');
      const dedupeWindowMs = notification.dedupeWindowMs
        ?? (isSessionExpired ? 5 * 60 * 1000 : 15 * 1000); // 5m vs 15s

      // Find an existing open notification matching the key within window
      const existingIdx = this.notifications.findIndex((n) => {
        const sameKey = computeDedupeKey(n) === incomingKey;
        const windowMs = n.dedupeWindowMs ?? dedupeWindowMs;
        const withinWindow = now - (n.createdAt || now) <= windowMs;
        return sameKey && withinWindow && n.isOpen !== false;
      });

      if (existingIdx !== -1) {
        // Refresh the existing notification instead of stacking a new one
        const existing = this.notifications[existingIdx];
        existing.createdAt = now;
        existing.isOpen = true;
        // If the incoming notification is sticky/longer timeout, adopt it
        if (notification.sticky) existing.sticky = true;
        if (notification.timeout && (!existing.timeout || notification.timeout > existing.timeout)) {
          existing.timeout = notification.timeout;
        }
        existing.dedupeWindowMs = notification.dedupeWindowMs ?? existing.dedupeWindowMs ?? dedupeWindowMs;
        existing.dedupeKey = existing.dedupeKey || incomingKey;
        // Recalculate offsets and bail
        this.recalculateOffsets();
        return existing.id;
      }

      const defaultNotification: NotificationParams = {
        title: '',
        message: '',
        theme: 'primary',
        position: 'br',
        autoClose: true,
        timeout: 2000,
        isOpen: true, // Default value for isOpen is true
        yOffset: isMobile ? 36 : 0, // Default offset is 0
        id: this.generateShortUID(), // Generate a 4-character UID for the notification
      };

      // Add NotificationState properties
      const stateProperties = {
        createdAt: now,
      };

      // Merge the default notification with the provided one and add state properties
      const finalNotification = {
        ...defaultNotification,
        ...stateProperties,
        ...notification,
        dedupeKey: notification.dedupeKey || incomingKey,
        dedupeWindowMs
      };

      // Add the new notification to the array
      this.notifications.push(finalNotification);

      // Recalculate yOffset for all notifications after adding the new one
      this.recalculateOffsets();

      if (finalNotification.autoClose) {
        setTimeout(() => {
          this.hideNotification(finalNotification);
        }, finalNotification.timeout);
      }
    },
    hideNotification(notification: NotificationParams) {
      const index = this.notifications.findIndex((n) => n.id === notification.id);
      if (index !== -1) {
        this.notifications.splice(index, 1);

        // Recalculate yOffset for all remaining notifications
        this.recalculateOffsets();
      }
    },
    recalculateOffsets() {
      this.notifications.forEach((existingNotification, index) => {
        existingNotification.yOffset = (this.notifications.length - index - 1) * 120;
      });
    },
    setTemporaryDemoProducts(value: boolean) {
      this.setState({ key: 'demoProducts', value });
    },
    clearTemporaryDemoProducts() {
      this.setState({ key: 'demoProducts', value: false });
    },
    startLoadingTimeout() {
      // Clear any existing timeout first
      this.clearLoadingTimeout();
      
      // Set the initial app loading start time
      this.setState({ key: 'loadingStartTime', value: Date.now() });
      
      // Start the 10-second timeout for initial app loading only
      // This is specifically for when isAppReady takes too long to become true
      const timeoutId = window.setTimeout(() => {
        this.handleLoadingTimeout();
      }, 10000); // 10 seconds
      
      this.setState({ key: 'loadingTimeoutId', value: timeoutId });
    },
    clearLoadingTimeout() {
      // Clear the initial app loading timeout
      if (this.loadingTimeoutId) {
        window.clearTimeout(this.loadingTimeoutId);
        this.setState({ key: 'loadingTimeoutId', value: null });
      }
      this.setState({ key: 'loadingStartTime', value: null });
    },
    handleLoadingTimeout() {
      // Show notification that initial app loading is taking too long
      this.showNotification({
        title: 'App Loading Taking Too Long',
        message: 'The app initialization is taking longer than expected. Refreshing the page now...',
        theme: 'warning',
        autoClose: false,
        timeout: 3000,
        isOpen: true
      });
      
      // Wait a moment for user to see the notification, then reload
      setTimeout(() => {
        // Force reload without cache to help resolve loading issues
        if ('caches' in window) {
          caches.keys().then(cacheNames => {
            cacheNames.forEach(cacheName => {
              caches.delete(cacheName);
            });
          });
        }
        window.location.reload();
      }, 3000);
    },
    
    // Enhanced Banner System Methods
    showBanner(banner: Partial<Banner> & { content: string }) {
      const bannerId = banner.id || uuidv4();
      const newBanner: Banner = {
        id: bannerId,
        content: banner.content,
        theme: banner.theme || 'info',
        priority: banner.priority || 'normal',
        position: banner.position || this.bannerState.defaultPosition,
        sticky: banner.sticky ?? true,
        dismissible: banner.dismissible ?? true,
        autoHide: banner.autoHide,
        icon: banner.icon,
        actions: banner.actions || [],
        createdAt: Date.now(),
        expiresAt: banner.expiresAt,
        metadata: banner.metadata || {}
      };

      // Dedupe strategy:
      // 1) If a banner with the same explicit id exists, update it instead of stacking
      // 2) If metadata.dedupeKey exists, ensure only a single banner with that key
      const dedupeKey = (newBanner.metadata && (newBanner.metadata as any).dedupeKey) as string | undefined;

      const existingIndexById = this.bannerState.banners.findIndex(b => b.id === newBanner.id);
      if (existingIndexById !== -1) {
        Object.assign(this.bannerState.banners[existingIndexById], newBanner, { createdAt: this.bannerState.banners[existingIndexById].createdAt });
        this.sortBanners();
        return newBanner.id;
      }

      if (dedupeKey) {
        const existingIndexByKey = this.bannerState.banners.findIndex(b => (b.metadata && (b.metadata as any).dedupeKey) === dedupeKey);
        if (existingIndexByKey !== -1) {
          Object.assign(this.bannerState.banners[existingIndexByKey], newBanner, { id: this.bannerState.banners[existingIndexByKey].id, createdAt: this.bannerState.banners[existingIndexByKey].createdAt });
          this.sortBanners();
          return this.bannerState.banners[existingIndexByKey].id;
        }
      }

      // Add banner to state
      this.bannerState.banners.push(newBanner);

      // Sort by priority and creation time (highest priority and most recent first)
      this.sortBanners();

      // Limit visible banners
      if (this.bannerState.banners.length > this.bannerState.maxVisible) {
        this.bannerState.banners = this.bannerState.banners.slice(0, this.bannerState.maxVisible);
      }

      // Set up auto-hide if specified
      if (newBanner.autoHide) {
        setTimeout(() => {
          this.hideBanner(newBanner.id);
        }, newBanner.autoHide);
      }

      return newBanner.id;
    },
    
    hideBanner(bannerId: string) {
      const index = this.bannerState.banners.findIndex(b => b.id === bannerId);
      if (index !== -1) {
        this.bannerState.banners.splice(index, 1);
      }
    },
    
    hideAllBanners() {
      this.bannerState.banners = [];
    },
    
    getBanner(bannerId: string) {
      return this.bannerState.banners.find(b => b.id === bannerId);
    },
    
    updateBanner(bannerId: string, updates: Partial<Banner>) {
      const banner = this.getBanner(bannerId);
      if (banner) {
        Object.assign(banner, updates);
        this.sortBanners();
      }
    },
    
    sortBanners() {
      const priorityOrder = { urgent: 4, high: 3, normal: 2, low: 1 };
      this.bannerState.banners.sort((a, b) => {
        const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
        if (priorityDiff !== 0) return priorityDiff;
        return b.createdAt - a.createdAt; // Most recent first for same priority
      });
    },
    
    // Legacy banner support (backward compatibility)
    showLegacyBanner(content: string, options: { sticky?: boolean } = {}) {
      return this.showBanner({
        content,
        theme: 'primary',
        sticky: options.sticky ?? true,
        priority: 'normal',
        dismissible: true
      });
    },
    
    hideLegacyBanner() {
      // Hide the legacy banner by setting the old state
      this.setState({ key: 'showDynamicBanner', value: false });
      this.setState({ key: 'dynamicBanner', value: null });
    },

    // Enhanced Notification System Methods
    showEnhancedNotification(params: Partial<NotificationParams> & { title: string; message: string }) {
      if (this.notificationSystem.globalPaused) return null;

      // Dedupe logic for enhanced notifications as well
      const now = Date.now();
      const safeLower = (val: unknown, fallback = ''): string => {
        if (typeof val === 'string') return val.toLowerCase();
        try {
          if (val && typeof val === 'object') {
            const maybeMessage = (val as any).message ?? (val as any).name;
            if (typeof maybeMessage === 'string') return maybeMessage.toLowerCase();
          }
          return String(val ?? fallback).toLowerCase();
        } catch {
          return String(fallback).toLowerCase();
        }
      };

      const computeDedupeKey = (n: Partial<NotificationParams>) => {
        if (n.dedupeKey) {
          return safeLower(n.dedupeKey, '');
        }
        const t = safeLower(n.title, '');
        const m = safeLower(n.message, '');
        const th = safeLower(n.theme || 'primary', 'primary');
        const p = safeLower(n.position || 'br', 'br');
        return `${t}|${m}|${th}|${p}`;
      };
      const incomingKey = computeDedupeKey(params);
      const isSessionExpired = safeLower(params.title, '').includes('session expired')
        || safeLower(params.message, '').includes('session expired');
      const dedupeWindowMs = params.dedupeWindowMs
        ?? (isSessionExpired ? 5 * 60 * 1000 : 15 * 1000);

      const existingEnhancedIdx = this.notificationSystem.notifications.findIndex((n) => {
        const sameKey = computeDedupeKey(n) === incomingKey;
        const windowMs = n.dedupeWindowMs ?? dedupeWindowMs;
        const withinWindow = now - (n.createdAt || now) <= windowMs;
        return sameKey && withinWindow && n.isOpen !== false;
      });
      if (existingEnhancedIdx !== -1) {
        const existing = this.notificationSystem.notifications[existingEnhancedIdx];
        existing.createdAt = now;
        existing.isOpen = true;
        if (params.sticky) existing.sticky = true;
        if (params.timeout && (!existing.timeout || params.timeout > existing.timeout)) {
          existing.timeout = params.timeout;
        }
        existing.dedupeWindowMs = params.dedupeWindowMs ?? existing.dedupeWindowMs ?? dedupeWindowMs;
        existing.dedupeKey = existing.dedupeKey || incomingKey;
        this.recalculateEnhancedOffsets();
        return existing.id;
      }

      const notification: NotificationState = {
        id: params.id || uuidv4(),
        title: params.title,
        message: params.message,
        theme: params.theme || 'primary',
        position: params.position || 'br',
        autoClose: params.autoClose ?? true,
        timeout: params.timeout || (params.priority === 'urgent' ? 10000 : params.priority === 'high' ? 5000 : 3000),
        isOpen: true,
        yOffset: 0,
        priority: params.priority || 'normal',
        variant: params.variant || 'default',
        dismissible: params.dismissible ?? true,
        sticky: params.sticky || false,
        stackIndex: params.stackIndex,
        maxWidth: params.maxWidth,
        persistent: params.persistent || false,
        actions: params.actions || [],
        createdAt: now,
        dedupeKey: params.dedupeKey || incomingKey,
        dedupeWindowMs
      };

      // Add to enhanced system
      this.notificationSystem.notifications.push(notification);

      // Also add to legacy system for backward compatibility
      this.notifications.push(notification);

      // Calculate proper stacking (with optional cleanup control)
      this.recalculateEnhancedOffsets(params.allowOverflow || false);

      // Set up auto-close
      if (notification.autoClose && !notification.sticky) {
        setTimeout(() => {
          this.hideEnhancedNotification(notification.id!);
        }, notification.timeout);
      }

      return notification.id;
    },

    hideEnhancedNotification(notificationId: string) {
      // Remove from enhanced system
      const enhancedIndex = this.notificationSystem.notifications.findIndex(n => n.id === notificationId);
      if (enhancedIndex !== -1) {
        this.notificationSystem.notifications.splice(enhancedIndex, 1);
      }

      // Remove from legacy system
      const legacyIndex = this.notifications.findIndex(n => n.id === notificationId);
      if (legacyIndex !== -1) {
        this.notifications.splice(legacyIndex, 1);
      }

      // Recalculate stacking
      this.recalculateEnhancedOffsets();
      this.recalculateOffsets(); // Legacy support
    },

    hideAllNotificationsByPosition(position: Position) {
      const toRemove = this.notificationSystem.notifications.filter(n => n.position === position);
      toRemove.forEach(notification => {
        if (notification.id) {
          this.hideEnhancedNotification(notification.id);
        }
      });
    },

    hideAllNotifications() {
      this.notificationSystem.notifications = [];
      this.notifications = [];
    },

    pauseNotifications() {
      this.notificationSystem.globalPaused = true;
    },

    resumeNotifications() {
      this.notificationSystem.globalPaused = false;
    },

    getNotificationsByPosition(position: Position): NotificationState[] {
      return this.notificationSystem.notifications.filter(n => n.position === position);
    },

    updateNotificationStackConfig(position: Position, config: Partial<PositionStackConfig>) {
      this.notificationSystem.stackConfigs[position] = {
        ...this.notificationSystem.stackConfigs[position],
        ...config
      };
      this.recalculateEnhancedOffsets();
    },

    recalculateEnhancedOffsets(allowOverflow = false) {
      // Group notifications by position
      const byPosition: Record<Position, NotificationState[]> = {
        'tr': [], 'tl': [], 'br': [], 'bl': [], 'tc': [], 'bc': []
      };

      this.notificationSystem.notifications.forEach(notification => {
        const pos = notification.position || 'br';
        byPosition[pos].push(notification);
      });

      // Calculate offsets for each position
      Object.entries(byPosition).forEach(([position, notifications]) => {
        const pos = position as Position;
        const config = this.notificationSystem.stackConfigs[pos];
        
        // Sort by priority and creation time
        notifications.sort((a, b) => {
          const priorityOrder = { urgent: 4, high: 3, normal: 2, low: 1 };
          const aPriority = priorityOrder[a.priority || 'normal'];
          const bPriority = priorityOrder[b.priority || 'normal'];
          
          if (aPriority !== bPriority) {
            return bPriority - aPriority; // Higher priority first
          }
          return b.createdAt - a.createdAt; // Newer first for same priority (latest clicks/notifications on top)
        });

        // Determine if we should use card stacking
        const shouldUseCardStacking = notifications.length >= config.cardStackThreshold;

        // Apply stacking offsets
        notifications.forEach((notification, index) => {
          // Set card mode properties
          notification.isCardMode = shouldUseCardStacking;
          notification.cardStackIndex = index;
          notification.zIndex = notifications.length - index; // Higher z-index for notifications on top

          if (shouldUseCardStacking) {
            // Card stacking mode - stack like cards with small offsets
            notification.cardOffsetX = index * config.cardOffsetX;
            notification.cardOffsetY = index * config.cardOffsetY;
            notification.yOffset = 0; // Base position, cards handle their own positioning
          } else {
            // Linear stacking mode - traditional spacing
            let offset = 0;
            
            if (config.stackDirection === 'up' || config.stackDirection === 'down') {
              // Vertical stacking
              offset = index * (config.spacing + this.getNotificationHeight(notification));
            } else {
              // Horizontal stacking (for future use)
              offset = index * (config.spacing + this.getNotificationWidth(notification));
            }

            notification.yOffset = offset;
            notification.cardOffsetX = 0;
            notification.cardOffsetY = 0;
          }
          
          notification.stackIndex = index;
        });

        // Limit visible notifications per position (unless overflow is allowed)
        if (!allowOverflow && notifications.length > config.maxVisible) {
          const excess = notifications.slice(config.maxVisible);
          excess.forEach(notification => {
            if (notification.id) {
              this.hideEnhancedNotification(notification.id);
            }
          });
        }
      });
    },

    getNotificationHeight(notification: NotificationState): number {
      // Estimate notification height based on variant and content
      const baseHeight = notification.variant === 'compact' ? 60 : 
                        notification.variant === 'expanded' ? 120 : 80;
      
      // Add height for actions
      if (notification.actions && notification.actions.length > 0) {
        return baseHeight + 40;
      }
      
      return baseHeight;
    },

    getNotificationWidth(notification: NotificationState): number {
      // Standard notification width
      return notification.maxWidth || 384; // max-w-sm = 384px
    },

    // Manual cleanup method for testing overflow behavior
    cleanupOverflowNotifications() {
      this.recalculateEnhancedOffsets(false); // Force cleanup
    },

    // Bring a notification to the front of its stack
    bringNotificationToFront(notificationId: string) {
      const notification = this.notificationSystem.notifications.find(n => n.id === notificationId);
      if (!notification || !notification.isCardMode) return;

      const position = notification.position || 'br';
      
      // Get all notifications at this position
      const positionNotifications = this.notificationSystem.notifications.filter(
        n => (n.position || 'br') === position
      );

      // If already at front (index 0), do nothing
      if (notification.cardStackIndex === 0) return;

      // Sort by current stack index to maintain order
      positionNotifications.sort((a, b) => (a.cardStackIndex || 0) - (b.cardStackIndex || 0));

      // Remove the clicked notification from its current position
      const clickedIndex = positionNotifications.findIndex(n => n.id === notificationId);
      if (clickedIndex === -1) return;

      const [clickedNotification] = positionNotifications.splice(clickedIndex, 1);
      
      // Insert at the front
      positionNotifications.unshift(clickedNotification);

      // Update the creation time to make it "newest" for proper sorting
      clickedNotification.createdAt = Date.now();

      // Recalculate offsets to trigger animations
      this.recalculateEnhancedOffsets();
    }
  },
});
